import { FC, LegacyRef, useMemo, useCallback } from 'react';
import { Radio, Flex, Heading } from '@highereducation/hen-ui';
import type { QuestionProps } from '@de/components/Step/Step';
import { useDynamicExperienceContext } from '@de/providers/DynamicExperienceProvider';
import useQuestionEvents from '@de/hooks/useQuestionEvents';
import { resolveQuestionTitle } from '@de/utils/resolveDynamicFilters';
import { OptionsLoadingPlaceholder } from '@de/components/Step/OptionsLoadingPlaceholder';

/**
 * RadioQuestion component using hen-ui design system.
 *
 * A radio-style question component built with hen-ui atomic components,
 * following EDU Design System standards. Displays selectable options with
 * visual radio button feedback.
 *
 * @example
 * <RadioQuestion
 *   id="preferredRole"
 *   options={[
 *     { optionId: '1', text: 'Student' },
 *     { optionId: '2', text: 'Faculty' },
 *     { optionId: '3', text: 'Staff' }
 *   ]}
 *   leadFieldAssociated="role"
 *   stepName="Role"
 * />
 */
const RadioQuestion: FC<QuestionProps> = ({
  options = [],
  title,
  isMultiQuestionChild = false,
  id = '',
  leadFieldAssociated,
  automaticNext = false,
  stepName,
  variant,
  isOptionsLoading = false,
}) => {
  const { updateFlowState, getStepMetadataById, handleStepForward, flowState } =
    useDynamicExperienceContext();
  const stepSelectedData = getStepMetadataById(id || '');
  const selectedOption = stepSelectedData?.option;

  const resolvedTitle = useMemo(() => {
    if (!title) return '';
    return resolveQuestionTitle(title, flowState);
  }, [title, flowState]);

  const { ref: visibilityRef, triggerFieldInputted } = useQuestionEvents({
    id,
    fieldType: 'radio',
    options,
    title,
    stepName,
    isMultiQuestionChild,
    variant,
    leadFieldAssociated,
  });

  const handleOptionChange = useCallback(
    (value: string) => {
      const option = options.find((opt) => opt.optionId === value);
      if (!option) return;

      const isSelected =
        selectedOption &&
        !Array.isArray(selectedOption) &&
        selectedOption.optionId === option.optionId;

      if (isSelected) {
        updateFlowState(id, {});
      } else {
        updateFlowState(id, { option, leadFieldAssociated });
      }

      triggerFieldInputted(option.text as string);

      if (automaticNext && !isMultiQuestionChild) {
        handleStepForward();
      }
    },
    [
      options,
      selectedOption,
      id,
      leadFieldAssociated,
      updateFlowState,
      isMultiQuestionChild,
      triggerFieldInputted,
      automaticNext,
      handleStepForward,
    ],
  );

  const radioOptions = options.map((option) => ({
    value: option.optionId || '',
    label: option.text as string,
  }));

  const selectedValue =
    selectedOption && !Array.isArray(selectedOption)
      ? selectedOption.optionId
      : '';

  return (
    <Flex
      direction={isMultiQuestionChild ? 'row' : 'column'}
      gap={isMultiQuestionChild ? 'xs' : 'md'}
      ref={visibilityRef as LegacyRef<HTMLDivElement>}
    >
      {title && isMultiQuestionChild && (
        <Heading variant='h6'>{resolvedTitle}</Heading>
      )}

      {isOptionsLoading ? (
        <OptionsLoadingPlaceholder />
      ) : (
        <Radio
          options={radioOptions}
          value={selectedValue}
          onValueChange={handleOptionChange}
          name={id}
        />
      )}
    </Flex>
  );
};

export default RadioQuestion;
